import { ApiResult, FieldType, ViewModelBase } from "@shoothill/core";
import { PurchaseOrderListModel, PurchaseOrderListModelDTO, PurchaseOrderListTableModel2 } from "./PurchaseOrderListModel";
import * as mobx from "mobx";
import { GenericIncludeDeleted } from "Globals/Models";
import { AppUrls } from "AppUrls";
import { PackageCategoriesResult } from "./PackageCategoriesResult";
import debounce from "lodash/debounce";
import { OrderType, ProjectDTO, RequisitionPOStatus, UserDTO } from "Views/Approval/PurchaseListViewModel";
import { RejectedOrderListFilterViewModel } from "./RejectedOrderListFilterViewModel";
import { csvAxiosRequestConfig, exportPDFWithGet } from "Utils/Utils";

export class RejectedOrderListViewModel extends ViewModelBase<PurchaseOrderListModel> {
    @mobx.observable public errorMessage: string = "";

    @mobx.observable
    public purchaseOrders: mobx.IObservableArray<PurchaseOrderListModel> = mobx.observable<PurchaseOrderListModel>([]);

    @mobx.computed
    public get getPurchaseOrdersList(): PurchaseOrderListTableModel2[] {
        return this.purchaseOrders.map((p) => {
            return p.toTableModel2();
        });
    }

    constructor() {
        super(new PurchaseOrderListModel(), false);
        this.setDecorators(PurchaseOrderListModel);
    }

    public handleCategoryChange = (val: number[] | null) => {
        RejectedOrderListFilterViewModel.Instance.setValue("categories", val);
        RejectedOrderListFilterViewModel.Instance.setValue("subCategories", []);
        this.GetAllWithFiltersAsync();
    };

    public handleRaisedByChange = (val: number[] | null) => {
        RejectedOrderListFilterViewModel.Instance.setValue("raisedByIds", val);
        this.GetAllWithFiltersAsync();
    };

    public handleProjectChange = (val: number[] | null) => {
        RejectedOrderListFilterViewModel.Instance.setValue("projects", val);
        this.GetAllWithFiltersAsync();
    };

    public handleSubCategoryChange = (val: number[] | null) => {
        RejectedOrderListFilterViewModel.Instance.setValue("subCategories", val);
        this.GetAllWithFiltersAsync();
    };

    public handleSearchChange = (val: string) => {
        RejectedOrderListFilterViewModel.Instance.setValue("searchText", val);

        if (val.length > 3 || val.length === 0) {
            this.reloadDataDelayed();
        }
    };

    public handleStatusChange = (val: number[] | null) => {
        RejectedOrderListFilterViewModel.Instance.setValue("statuses", val);
        this.GetAllWithFiltersAsync();
    };

    public handleCellChange = (val: number[] | null) => {
        RejectedOrderListFilterViewModel.Instance.setValue("cells", val);
        this.GetAllWithFiltersAsync();
    };

    public handleOrderTypeChange = (val: number[] | null) => {
        RejectedOrderListFilterViewModel.Instance.setValue("orderTypes", val);
        this.GetAllWithFiltersAsync();
    };

    private reloadDataDelayed = debounce(() => {
        this.GetAllWithFiltersAsync();
    }, 300);

    @mobx.action
    public cleanUp = () => {
        this.reloadDataDelayed.cancel();
    };

    @mobx.action
    public clear = () => {
        RejectedOrderListFilterViewModel.Instance.model.reset();
        this.GetAllWithFiltersAsync();
    };

    @mobx.action
    public setPurchaseOrders(purchaseOrders: PurchaseOrderListModelDTO[]) {
        this.purchaseOrders.clear();
        purchaseOrders.forEach((item, index) => {
            let domainModel = this.purchaseOrders.find((c) => c.id === item.id);

            if (!domainModel) {
                domainModel = new PurchaseOrderListModel();

                domainModel.fromDto(item);
                this.purchaseOrders.push(domainModel);
            }
        });
    }

    @mobx.action
    public async loadPurchaseOrderListAsync(): Promise<ApiResult<PurchaseOrderListModelDTO[]>> {
        const request: GenericIncludeDeleted = {
            includeDeleted: true,
        };

        let apiResult = await this.Post<PurchaseOrderListModelDTO[]>(AppUrls.Server.PurchaseOrder.GetAll, request);

        if (apiResult.wasSuccessful) {
            mobx.runInAction(() => {
                this.setPurchaseOrders(apiResult.payload);
            });
        }
        return apiResult;
    }

    @mobx.action
    public GetAllWithFiltersAsync = async (): Promise<ApiResult<PurchaseOrderListAndRelatedDTO>> => {
        let paramsViewModel = RejectedOrderListFilterViewModel.Instance;
        let params = paramsViewModel.model.toDto();

        const apiResult = await this.Post<PurchaseOrderListAndRelatedDTO>(AppUrls.Server.PurchaseOrder.GetAllWithFilters, params);

        if (apiResult.wasSuccessful) {
            this.setPurchaseOrders(apiResult.payload.purchaseOrders);
            RejectedOrderListFilterViewModel.Instance.setCategories(apiResult.payload.packageCategories, true);
        }

        return apiResult;
    };

    @mobx.action
    public apiGeneratePOPDF = async (id: string) => {
        await exportPDFWithGet(AppUrls.Server.PurchaseOrder.GeneratePOPDFById, { id: id }, await this.getConfig(true, csvAxiosRequestConfig));
    };

    public async isFieldValid(fieldName: keyof FieldType<PurchaseOrderListModel>): Promise<boolean> {
        const { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}

export type PurchaseOrderListAndRelatedDTO = {
    purchaseOrders: PurchaseOrderListModelDTO[];
    packageCategories: PackageCategoriesResult;
    statuses: RequisitionPOStatus[];
    orderTypes: OrderType[];
    purchaseUsers: UserDTO[];
    projects: ProjectDTO[];
};
